"use client";
import { useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { TrendingUp, ArrowRight, LayoutGrid } from "lucide-react";
import { INVESTMENT_SECTORS_PATH } from "@/lib/site";

const tickers = [
  { symbol: "BTC", price: "$67,234.50", change: "+3.24%", up: true },
  { symbol: "ETH", price: "$3,512.18", change: "+1.87%", up: true },
  { symbol: "BNB", price: "$412.90", change: "-0.54%", up: false },
  { symbol: "SOL", price: "$178.44", change: "+5.10%", up: true },
  { symbol: "AVAX", price: "$38.72", change: "+2.33%", up: true },
  { symbol: "MATIC", price: "$1.04", change: "-1.22%", up: false },
  { symbol: "LINK", price: "$18.90", change: "+4.65%", up: true },
  { symbol: "ARB", price: "$1.21", change: "+7.80%", up: true },
  { symbol: "XAU", price: "$2,344.00", change: "+0.82%", up: true },
  { symbol: "XAG", price: "$30.15", change: "+1.14%", up: true },
];

const doubled = [...tickers, ...tickers];

export default function Hero() {
  const router = useRouter();
  /** null = unknown / still checking, true = signed in, false = guest. */
  const [isAuthed, setIsAuthed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/session", { credentials: "include" });
        if (cancelled) return;
        setIsAuthed(res.ok);
      } catch {
        if (!cancelled) setIsAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isAuthed === null) return;
    router.prefetch(isAuthed ? "/user/investments" : "/user");
  }, [isAuthed, router]);

  const investHref = isAuthed === true ? "/user/investments" : "/user";

  const handleStartInvesting = useCallback(
    async (e) => {
      e.preventDefault();
      let authed = isAuthed;
      if (authed === null) {
        try {
          const res = await fetch("/api/user/session", { credentials: "include" });
          authed = res.ok;
        } catch {
          authed = false;
        }
        setIsAuthed(authed);
      }
      router.push(authed ? "/user/investments" : "/user");
    },
    [isAuthed, router]
  );

  return (
    <section
      id="home"
      className="hero-dark relative flex min-h-screen flex-col overflow-hidden pt-16 sm:pt-20"
    >
      <div className="relative flex flex-1 flex-col justify-center">
        <div className="pointer-events-none absolute inset-0">
          <Image
            src="/hero/hero.webp"
            alt=""
            fill
            priority
            sizes="100vw"
            className="object-cover object-center"
            aria-hidden
          />
          <div className="absolute inset-0 bg-linear-to-r from-black/80 via-black/45 to-black/10" />
          <div className="absolute inset-0 bg-linear-to-t from-black/70 via-transparent to-black/30" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center 2xl:px-6 xl:px-5 lg:px-4 md:px-4 px-4 py-14 sm:py-16">
          <div className="max-w-2xl lg:max-w-[640px]">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.5 }}
              className="mb-7 inline-flex"
            >
              <div className="flex items-center gap-2 rounded-full border border-accent-amber/30 bg-accent-amber/10 px-4 py-1.5 backdrop-blur-sm">
                <TrendingUp size={14} className="text-accent-amber" />
                <span className="text-xs font-medium text-accent-amber sm:text-sm">
                  High-Yield Investment Platform
                </span>
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12, duration: 0.55 }}
              className="font-display mb-6 text-[clamp(2.25rem,6vw,4.25rem)] leading-[1.05] tracking-tight text-text-primary drop-shadow-[0_4px_32px_rgba(0,0,0,0.9)]"
            >
              Your Gateway to
              <br />
              <span className="text-ember">High-Yield</span> Investments
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.55 }}
              className="mb-10 max-w-lg text-base leading-relaxed text-text-primary/90 drop-shadow-[0_2px_16px_rgba(0,0,0,0.85)] sm:text-lg"
            >
              Earn consistently through expertly managed{" "}
              <Link
                href={INVESTMENT_SECTORS_PATH}
                className="font-medium text-accent-amber transition hover:text-accent-orange"
              >
                investment sectors
              </Link>
              .
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.55 }}
              className="flex flex-wrap justify-center gap-4 lg:justify-start"
            >
              <a
                href={investHref}
                onClick={handleStartInvesting}
                className="group inline-flex items-center gap-2 rounded-xl bg-linear-to-br from-accent-amber to-accent-orange px-8 py-4 font-display text-base font-bold text-white shadow-[0_8px_32px_rgba(245,158,11,0.35)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(245,158,11,0.45)]"
              >
                Start Investing
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </a>

              <a
                href="#plans"
                className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-bg-card/40 px-8 py-4 font-display text-base font-semibold text-text-primary backdrop-blur-sm transition hover:border-accent-amber hover:text-accent-amber"
              >
                View Plans
              </a>

              <Link
                href={INVESTMENT_SECTORS_PATH}
                className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-bg-card/40 px-8 py-4 font-display text-base font-semibold text-text-primary backdrop-blur-sm transition hover:border-accent-amber hover:text-accent-amber"
              >
                <LayoutGrid size={18} aria-hidden />
                Investment sectors
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full overflow-hidden">
        <div className="border-y border-border bg-bg-card py-3">
          <div className="marquee-track">
            {doubled.map((ticker, i) => (
              <div
                key={i}
                className="flex whitespace-nowrap border-r border-border px-7"
              >
                <span className="font-display text-xs font-bold text-text-primary sm:text-sm">
                  {ticker.symbol}
                </span>
                <span className="ml-2 text-xs text-text-muted sm:text-sm">
                  {ticker.price}
                </span>
                <span
                  className={`ml-2 text-xs font-semibold ${ticker.up ? "text-green-500" : "text-red-500"}`}
                >
                  {ticker.change}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
